import { formatPriceCents } from '../automations.format';
import { AutomationDetailsViewModel } from './AutomationDetails.viewModel';

type SavingSummary =
  | { kind: 'saving'; text: string }
  | { kind: 'no-saving'; text: string }
  | { kind: 'unavailable'; text: string };

type MonthlyToAnnualConfigViewModel = {
  summary: SavingSummary;
  includePushes: boolean;
  setIncludePushes: (includePushes: boolean) => void;
};

/** The saving is the price difference itself, no coupon (GRW-571). */
const savingSummary = (
  annual: string,
  yearlyAtMonthly: string,
  saving: string,
  percent: string,
) =>
  `Members pay ${annual} for the year instead of ${yearlyAtMonthly}, so they save ${saving} (${percent}).`;

const noSavingSummary = (annual: string, yearlyAtMonthly: string) =>
  `Members pay ${annual} for the year and ${yearlyAtMonthly} on monthly billing. Your annual plan is not cheaper, so the emails cannot promise a saving.`;

export const toSavingSummary = (
  monthlyPrice: number | null,
  annualPrice: number | null,
): SavingSummary => {
  if (monthlyPrice === null || annualPrice === null) {
    return {
      kind: 'unavailable',
      text: 'Your subscription needs both a monthly and an annual price before this can run.',
    };
  }
  const yearlyAtMonthly = monthlyPrice * 12;
  const saving = yearlyAtMonthly - annualPrice;
  if (saving <= 0) {
    return {
      kind: 'no-saving',
      text: noSavingSummary(
        formatPriceCents(annualPrice),
        formatPriceCents(yearlyAtMonthly),
      ),
    };
  }
  const percent = `${Math.round((saving / yearlyAtMonthly) * 100)}%`;
  return {
    kind: 'saving',
    text: savingSummary(
      formatPriceCents(annualPrice),
      formatPriceCents(yearlyAtMonthly),
      formatPriceCents(saving),
      percent,
    ),
  };
};

export const useMonthlyToAnnualConfigViewModel = (
  details: AutomationDetailsViewModel,
): MonthlyToAnnualConfigViewModel => ({
  summary: toSavingSummary(details.monthlyPrice, details.annualPrice),
  includePushes: details.draft.includePushes,
  setIncludePushes: details.setIncludePushes,
});
